/**
 * Memory Center — 增强版分层记忆管理中心
 *
 * 功能模块：
 * 1. 记忆分类概览（短期/长期/偏好/场景）
 * 2. 用户偏好标签管理
 * 3. 历史行为记录列表
 * 4. AI 学习偏好展示（标签云）
 * 5. 记忆详情面板
 *
 * 设计风格：暗夜蓝 + 萤火暖金
 */

import { useState, useRef, useEffect, useCallback, CSSProperties, ReactNode, useMemo } from 'react';
import { message } from 'antd';

/* ═══════════════════════════════════════════
   类型定义
   ═══════════════════════════════════════════ */

/** 记忆分类类型 */
type MemoryCategory = 'short' | 'long' | 'preference' | 'episodic';

/** 用户偏好标签 */
interface PreferenceTag {
  id: string;
  label: string;
  category: 'lifestyle' | 'dining' | 'travel' | 'budget' | 'social';
  strength: number; // 0-100，偏好强度
  source: 'explicit' | 'learned'; // explicit=用户显式设置, learned=AI学习
  createdAt: number;
}

/** 历史行为记录 */
interface BehaviorRecord {
  id: string;
  type: 'search' | 'browse' | 'favorite' | 'booking' | 'share';
  title: string;
  description: string;
  timestamp: number;
  metadata?: Record<string, string | number>;
}

/** AI 学习到的偏好 */
interface LearnedPreference {
  id: string;
  label: string;
  confidence: number; // 0-1
  frequency: number; // 出现次数
  lastSeen: number;
  relatedTags: string[];
}

/** 记忆条目 */
interface MemoryItem {
  id: string;
  category: MemoryCategory;
  content: string;
  weight: number;
  createdAt: number;
  expiresAt?: number;
  metadata?: Record<string, unknown>;
}

/* ═══════════════════════════════════════════
   Mock 数据 — 扩展版
   ═══════════════════════════════════════════ */

const INITIAL_PREFERENCES: PreferenceTag[] = [
  { id: 'p1', label: '安静酒店', category: 'travel', strength: 92, source: 'explicit', createdAt: Date.now() - 86400000 * 30 },
  { id: 'p2', label: '不喜早起', category: 'lifestyle', strength: 88, source: 'learned', createdAt: Date.now() - 86400000 * 15 },
  { id: 'p3', label: '预算中等', category: 'budget', strength: 75, source: 'explicit', createdAt: Date.now() - 86400000 * 45 },
  { id: 'p4', label: '喜欢咖啡', category: 'dining', strength: 95, source: 'learned', createdAt: Date.now() - 86400000 * 7 },
  { id: 'p5', label: '喜欢拍照', category: 'lifestyle', strength: 82, source: 'learned', createdAt: Date.now() - 86400000 * 20 },
  { id: 'p6', label: '不喜欢排队', category: 'social', strength: 90, source: 'explicit', createdAt: Date.now() - 86400000 * 60 },
  { id: 'p7', label: '日料偏好', category: 'dining', strength: 78, source: 'learned', createdAt: Date.now() - 86400000 * 10 },
  { id: 'p8', label: '周末出行', category: 'travel', strength: 70, source: 'learned', createdAt: Date.now() - 86400000 * 25 },
];

const INITIAL_BEHAVIORS: BehaviorRecord[] = [
  { id: 'b1', type: 'search', title: '搜索: 上海日料餐厅推荐', description: '筛选了 28 家日料餐厅，关注评分和氛围', timestamp: Date.now() - 3600000 * 2, metadata: { count: '28家' } },
  { id: 'b2', type: 'browse', title: '浏览: 月見·庭院日料', description: '查看详情 4 分钟，关注了庭院景观和套餐', timestamp: Date.now() - 3600000 * 5, metadata: { duration: '4min' } },
  { id: 'b3', type: 'favorite', title: '收藏: 湖畔咖啡', description: '标记为常去咖啡店，喜欢手冲品类', timestamp: Date.now() - 3600000 * 8, metadata: { type: '咖啡店' } },
  { id: 'b4', type: 'search', title: '搜索: 周末亲子活动', description: '查找适合带娃的户外活动场所', timestamp: Date.now() - 86400000 * 1, metadata: { result: '12项' } },
  { id: 'b5', type: 'booking', title: '预约: 周六晚餐位', description: '预约月見·庭院日料 18:30 景观位', timestamp: Date.now() - 86400000 * 2, metadata: { status: '已确认' } },
  { id: 'b6', type: 'share', title: '分享: 云南旅行攻略', description: '将自定义旅行路线分享给朋友', timestamp: Date.now() - 86400000 * 3, metadata: { target: '3人' } },
  { id: 'b7', type: 'browse', title: '浏览: 西湖景区门票', description: '查看周末门票价格和预约情况', timestamp: Date.now() - 86400000 * 4, metadata: { season: '秋季' } },
  { id: 'b8', type: 'search', title: '搜索: 商务出行酒店', description: '筛选静音、近地铁、有商务中心的酒店', timestamp: Date.now() - 86400000 * 5, metadata: { filter: '3项' } },
];

const LEARNED_PREFERENCES: LearnedPreference[] = [
  { id: 'l1', label: '手冲咖啡 > 美式', confidence: 0.94, frequency: 12, lastSeen: Date.now() - 86400000, relatedTags: ['喜欢咖啡', '品质优先'] },
  { id: 'l2', label: '晚餐时间偏好 18:30-19:30', confidence: 0.88, frequency: 8, lastSeen: Date.now() - 86400000 * 2, relatedTags: ['不喜早起', '规律作息'] },
  { id: 'l3', label: '餐厅选择: 评分 > 4.5', confidence: 0.91, frequency: 15, lastSeen: Date.now() - 3600000 * 5, relatedTags: ['品质优先', '日料偏好'] },
  { id: 'l4', label: '步行距离: < 2km', confidence: 0.82, frequency: 6, lastSeen: Date.now() - 86400000, relatedTags: ['市中心', '便利优先'] },
  { id: 'l5', label: '周末偏好户外活动', confidence: 0.76, frequency: 5, lastSeen: Date.now() - 86400000 * 7, relatedTags: ['周末出行', '喜欢拍照'] },
  { id: 'l6', label: '预算区间: ¥200-800/餐', confidence: 0.85, frequency: 10, lastSeen: Date.now() - 86400000 * 3, relatedTags: ['预算中等', '品质优先'] },
  { id: 'l7', label: '环境偏好: 安静/私密', confidence: 0.93, frequency: 9, lastSeen: Date.now() - 86400000 * 2, relatedTags: ['安静酒店', '不喜欢排队'] },
  { id: 'l8', label: '出行方式: 地铁优先', confidence: 0.79, frequency: 7, lastSeen: Date.now() - 86400000 * 4, relatedTags: ['市中心', '便利优先'] },
  { id: 'l9', label: '信息获取: 先看差评', confidence: 0.72, frequency: 4, lastSeen: Date.now() - 86400000 * 6, relatedTags: ['谨慎决策', '品质优先'] },
  { id: 'l10', label: '季节适配: 偏好秋季出行', confidence: 0.68, frequency: 3, lastSeen: Date.now() - 86400000 * 15, relatedTags: ['周末出行', '喜欢拍照'] },
];

const MEMORY_ITEMS: MemoryItem[] = [
  // 短期记忆
  { id: 'm1', category: 'short', content: '当前会话: 周末约会场景规划中', weight: 1.0, createdAt: Date.now() },
  { id: 'm2', category: 'short', content: '预算约束: ¥500 以内', weight: 0.9, createdAt: Date.now() },
  { id: 'm3', category: 'short', content: '地理范围: 市中心 5km', weight: 0.85, createdAt: Date.now() },
  { id: 'm4', category: 'short', content: '时间窗口: 今晚 18:00-22:00', weight: 0.8, createdAt: Date.now() },
  // 长期记忆
  { id: 'm5', category: 'long', content: '月消费均值: ¥8,500', weight: 0.7, createdAt: Date.now() - 86400000 * 30 },
  { id: 'm6', category: 'long', content: '累计完成订单: 12 次', weight: 0.65, createdAt: Date.now() - 86400000 * 60 },
  { id: 'm7', category: 'long', content: '常见城市: 上海、杭州、北京', weight: 0.6, createdAt: Date.now() - 86400000 * 90 },
  // 偏好记忆
  { id: 'm8', category: 'preference', content: '咖啡偏好: 手冲 > 美式 > 拿铁', weight: 0.95, createdAt: Date.now() - 86400000 * 7 },
  { id: 'm9', category: 'preference', content: '餐厅氛围: 安静私密 > 热闹', weight: 0.9, createdAt: Date.now() - 86400000 * 14 },
  { id: 'm10', category: 'preference', content: '排队容忍度: 最长 15 分钟', weight: 0.88, createdAt: Date.now() - 86400000 * 21 },
  // 场景记忆
  { id: 'm11', category: 'episodic', content: '2026-07: 亲子迪士尼一日游', weight: 0.7, createdAt: Date.now() - 86400000 * 45 },
  { id: 'm12', category: 'episodic', content: '2026-06: 云南一周旅行', weight: 0.65, createdAt: Date.now() - 86400000 * 90 },
  { id: 'm13', category: 'episodic', content: '2026-05: 北京商务出差', weight: 0.6, createdAt: Date.now() - 86400000 * 120 },
  { id: 'm14', category: 'episodic', content: '2026-04: 西湖春游野餐', weight: 0.55, createdAt: Date.now() - 86400000 * 150 },
];

/* ═══════════════════════════════════════════
   记忆分类配置
   ═══════════════════════════════════════════ */

interface CategoryConfig {
  id: MemoryCategory;
  name: string;
  icon: string;
  color: string;
  description: string;
  count: number;
}

/* ═══════════════════════════════════════════
   工具函数
   ═══════════════════════════════════════════ */

function formatTimeAgo(timestamp: number): string {
  const diff = Date.now() - timestamp;
  if (diff < 60000) return '刚刚';
  if (diff < 3600000) return `${Math.floor(diff / 60000)} 分钟前`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)} 小时前`;
  if (diff < 86400000 * 7) return `${Math.floor(diff / 86400000)} 天前`;
  return `${Math.floor(diff / (86400000 * 30))} 月前`;
}

function getCategoryColor(category: string): string {
  const colors: Record<string, string> = {
    lifestyle: '#e8d28a',
    dining: '#fbbf24',
    travel: '#34d399',
    budget: '#f87171',
    social: '#818cf8',
  };
  return colors[category] || '#a78bfa';
}

function getBehaviorIcon(type: string): string {
  const icons: Record<string, string> = {
    search: '🔍',
    browse: '👁️',
    favorite: '❤️',
    booking: '📅',
    share: '🔗',
  };
  return icons[type] || '📌';
}

/* ═══════════════════════════════════════════
   展开内容容器
   ═══════════════════════════════════════════ */

interface ExpandableContentProps {
  expanded: boolean;
  children: ReactNode;
}

function ExpandableContent({ expanded, children }: ExpandableContentProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState('0px');

  useEffect(() => {
    if (expanded && contentRef.current) {
      setMaxHeight(`${contentRef.current.scrollHeight}px`);
    } else {
      setMaxHeight('0px');
    }
  }, [expanded]);

  useEffect(() => {
    if (expanded && contentRef.current) {
      const timer = setTimeout(() => {
        if (contentRef.current) {
          setMaxHeight(`${contentRef.current.scrollHeight}px`);
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [expanded]);

  return (
    <div
      style={{
        maxHeight,
        overflow: 'hidden',
        transition: 'max-height 320ms cubic-bezier(0.4, 0, 0.2, 1), opacity 260ms cubic-bezier(0.4, 0, 0.2, 1)',
        opacity: expanded ? 1 : 0,
      }}
    >
      <div ref={contentRef}>{children}</div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   偏好标签组件
   ═══════════════════════════════════════════ */

interface PreferenceTagCloudProps {
  preferences: PreferenceTag[];
  onRemove: (id: string) => void;
  onAdd: (label: string, category: PreferenceTag['category']) => void;
  editable: boolean;
}

function PreferenceTagCloud({ preferences, onRemove, onAdd, editable }: PreferenceTagCloudProps) {
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newCategory, setNewCategory] = useState<PreferenceTag['category']>('lifestyle');

  const grouped = useMemo(() => {
    const groups: Record<string, PreferenceTag[]> = {};
    preferences.forEach((p) => {
      if (!groups[p.category]) groups[p.category] = [];
      groups[p.category].push(p);
    });
    return groups;
  }, [preferences]);

  const categoryNames: Record<string, string> = {
    lifestyle: '生活方式',
    dining: '餐饮偏好',
    travel: '出行习惯',
    budget: '预算倾向',
    social: '社交偏好',
  };

  const tagCloudStyle: CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
    marginTop: 16,
  };

  const renderTag = (tag: PreferenceTag) => {
    const categoryColor = getCategoryColor(tag.category);
    const opacity = 0.4 + (tag.strength / 100) * 0.6;
    const scale = 0.9 + (tag.strength / 100) * 0.3;

    return (
      <div
        key={tag.id}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '8px 16px',
          borderRadius: 24,
          fontSize: 13,
          color: categoryColor,
          background: `${categoryColor}10`,
          border: `1px solid ${categoryColor}30`,
          opacity,
          transform: `scale(${scale})`,
          transition: 'all 220ms cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          lineHeight: 1.4,
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.opacity = '1';
          (e.currentTarget as HTMLElement).style.transform = `scale(${scale + 0.05})`;
          (e.currentTarget as HTMLElement).style.boxShadow = `0 0 12px ${categoryColor}20`;
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.opacity = String(opacity);
          (e.currentTarget as HTMLElement).style.transform = `scale(${scale})`;
          (e.currentTarget as HTMLElement).style.boxShadow = 'none';
        }}
      >
        <span>{tag.label}</span>
        <span style={{ fontSize: 11, opacity: 0.6 }}>{tag.strength}%</span>
        {tag.source === 'learned' && (
          <span style={{ fontSize: 10, opacity: 0.5, fontStyle: 'italic' }}>AI</span>
        )}
        {editable && (
          <span
            onClick={(e) => {
              e.stopPropagation();
              onRemove(tag.id);
            }}
            style={{
              cursor: 'pointer',
              marginLeft: 4,
              fontSize: 12,
              opacity: 0.5,
              transition: 'opacity 150ms',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.opacity = '0.5'; }}
          >
            x
          </span>
        )}
      </div>
    );
  };

  return (
    <div>
      {Object.entries(grouped).map(([category, tags]) => (
        <div key={category} style={{ marginBottom: 20 }}>
          <div
            style={{
              fontSize: 12,
              color: 'var(--text-muted)',
              marginBottom: 8,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              fontWeight: 500,
            }}
          >
            {categoryNames[category] || category}
          </div>
          <div style={tagCloudStyle}>{tags.map(renderTag)}</div>
        </div>
      ))}

      {editable && (
        <div style={{ marginTop: 16 }}>
          {!adding ? (
            <button
              onClick={() => setAdding(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 16px',
                borderRadius: 24,
                fontSize: 13,
                color: 'var(--accent)',
                background: 'var(--accent-soft)',
                border: '1px dashed var(--accent)',
                cursor: 'pointer',
                transition: 'all 200ms',
              }}
            >
              + 添加偏好
            </button>
          ) : (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                autoFocus
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="输入偏好标签..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newLabel.trim()) {
                    onAdd(newLabel.trim(), newCategory);
                    setNewLabel('');
                    setAdding(false);
                  }
                  if (e.key === 'Escape') {
                    setNewLabel('');
                    setAdding(false);
                  }
                }}
                style={{
                  padding: '8px 14px',
                  borderRadius: 20,
                  border: '1px solid var(--border-default)',
                  background: 'var(--surface-1)',
                  color: 'var(--text-primary)',
                  fontSize: 13,
                  outline: 'none',
                  width: 160,
                }}
              />
              <select
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value as PreferenceTag['category'])}
                style={{
                  padding: '8px 12px',
                  borderRadius: 20,
                  border: '1px solid var(--border-default)',
                  background: 'var(--surface-1)',
                  color: 'var(--text-primary)',
                  fontSize: 13,
                  outline: 'none',
                  cursor: 'pointer',
                }}
              >
                <option value="lifestyle">生活方式</option>
                <option value="dining">餐饮偏好</option>
                <option value="travel">出行习惯</option>
                <option value="budget">预算倾向</option>
                <option value="social">社交偏好</option>
              </select>
              <button
                onClick={() => {
                  if (newLabel.trim()) {
                    onAdd(newLabel.trim(), newCategory);
                    setNewLabel('');
                    setAdding(false);
                  }
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: 20,
                  background: 'var(--accent)',
                  color: 'var(--night-950)',
                  border: 'none',
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                保存
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   历史行为记录组件
   ═══════════════════════════════════════════ */

interface BehaviorHistoryListProps {
  records: BehaviorRecord[];
}

function BehaviorHistoryList({ records }: BehaviorHistoryListProps) {
  const [filter, setFilter] = useState<string>('all');

  const filteredRecords = useMemo(() => {
    if (filter === 'all') return records;
    return records.filter((r) => r.type === filter);
  }, [records, filter]);

  const filterButtons = [
    { key: 'all', label: '全部' },
    { key: 'search', label: '搜索' },
    { key: 'browse', label: '浏览' },
    { key: 'favorite', label: '收藏' },
    { key: 'booking', label: '预约' },
    { key: 'share', label: '分享' },
  ];

  return (
    <div>
      {/* 筛选按钮 */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {filterButtons.map((btn) => (
          <button
            key={btn.key}
            onClick={() => setFilter(btn.key)}
            style={{
              padding: '6px 14px',
              borderRadius: 20,
              fontSize: 12,
              border: `1px solid ${filter === btn.key ? 'var(--accent)' : 'var(--border-default)'}`,
              background: filter === btn.key ? 'var(--accent-glow)' : 'transparent',
              color: filter === btn.key ? 'var(--accent)' : 'var(--text-secondary)',
              cursor: 'pointer',
              transition: 'all 200ms',
            }}
          >
            {btn.label}
          </button>
        ))}
      </div>

      {/* 记录列表 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {filteredRecords.map((record, index) => (
          <div
            key={record.id}
            className="animate-fade-up"
            style={{
              animationDelay: `${index * 60}ms`,
              padding: '14px 18px',
              borderRadius: 12,
              background: 'var(--surface-1)',
              border: '1px solid var(--border-subtle)',
              transition: 'all 220ms',
              cursor: 'default',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-hover)';
              (e.currentTarget as HTMLElement).style.background = 'var(--surface-2)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-subtle)';
              (e.currentTarget as HTMLElement).style.background = 'var(--surface-1)';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 16 }}>{getBehaviorIcon(record.type)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 14,
                    color: 'var(--text-primary)',
                    fontWeight: 500,
                    marginBottom: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {record.title}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: 'var(--text-muted)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {record.description}
                </div>
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--text-muted)',
                  whiteSpace: 'nowrap',
                  marginLeft: 12,
                }}
              >
                {formatTimeAgo(record.timestamp)}
              </div>
            </div>
            {record.metadata && (
              <div style={{ display: 'flex', gap: 8, marginTop: 8, marginLeft: 26 }}>
                {Object.entries(record.metadata).map(([key, value]) => (
                  <span
                    key={key}
                    style={{
                      fontSize: 11,
                      padding: '2px 8px',
                      borderRadius: 10,
                      background: 'var(--accent-soft)',
                      color: 'var(--accent)',
                    }}
                  >
                    {key}: {value}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   AI 学习偏好组件
   ═══════════════════════════════════════════ */

interface LearnedPreferencesProps {
  preferences: LearnedPreference[];
}

function LearnedPreferencesCloud({ preferences }: LearnedPreferencesProps) {
  const maxFreq = Math.max(...preferences.map((p) => p.frequency));

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 16,
          fontSize: 12,
          color: 'var(--text-muted)',
        }}
      >
        <span style={{ fontSize: 14 }}>✨</span>
        <span>基于 {INITIAL_BEHAVIORS.length} 条行为记录自动学习 · 置信度 = 偏好可靠程度</span>
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        {preferences
          .sort((a, b) => b.confidence - a.confidence)
          .map((pref, index) => {
            const size = 12 + (pref.frequency / maxFreq) * 6;
            const opacity = 0.5 + pref.confidence * 0.5;

            return (
              <div
                key={pref.id}
                className="animate-fade-up"
                style={{
                  animationDelay: `${index * 50}ms`,
                  display: 'inline-flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 4,
                  padding: '12px 18px',
                  borderRadius: 14,
                  background: 'var(--surface-1)',
                  border: '1px solid var(--border-subtle)',
                  transition: 'all 220ms',
                  minWidth: 100,
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.borderColor = 'var(--accent)';
                  (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 16px rgba(232, 210, 138, 0.1)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-subtle)';
                  (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                  (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                }}
              >
                <div
                  style={{
                    fontSize: size,
                    fontWeight: 600,
                    color: 'var(--accent)',
                    opacity,
                    lineHeight: 1.2,
                  }}
                >
                  {pref.label}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--text-muted)',
                    display: 'flex',
                    gap: 6,
                    alignItems: 'center',
                  }}
                >
                  <span>置信 {Math.round(pref.confidence * 100)}%</span>
                  <span style={{ opacity: 0.4 }}>|</span>
                  <span>×{pref.frequency}</span>
                </div>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
                  {pref.relatedTags.slice(0, 3).map((tag) => (
                    <span
                      key={tag}
                      style={{
                        fontSize: 10,
                        padding: '1px 6px',
                        borderRadius: 8,
                        background: 'var(--firefly-soft)',
                        color: 'var(--firefly)',
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   记忆分类条目组件
   ═══════════════════════════════════════════ */

interface MemoryCategorySectionProps {
  category: CategoryConfig;
  items: MemoryItem[];
  expanded: boolean;
  onToggle: () => void;
}

function MemoryCategorySection({ category, items, expanded, onToggle }: MemoryCategorySectionProps) {
  const [hovered, setHovered] = useState(false);

  const cardStyle: CSSProperties = {
    position: 'relative',
    borderRadius: 16,
    border: `1px solid ${hovered || expanded ? category.color : 'var(--border-default)'}`,
    background: hovered ? 'var(--surface-2)' : 'var(--surface-1)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    padding: '24px 22px 20px',
    cursor: 'pointer',
    transition: 'all 220ms cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'hidden',
  };

  return (
    <div style={cardStyle} onClick={onToggle} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: `linear-gradient(90deg, ${category.color}, transparent)`,
          opacity: hovered || expanded ? 1 : 0.4,
          transition: 'opacity 220ms',
        }}
      />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: 12,
              background: `${category.color}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 20,
            }}
          >
            {category.icon}
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2 }}>
              {category.name}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{category.description}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span
            style={{
              fontSize: 20,
              fontWeight: 600,
              color: category.color,
            }}
          >
            {items.length}
          </span>
          <span
            style={{
              fontSize: 14,
              color: 'var(--text-muted)',
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 260ms',
            }}
          >
            ▾
          </span>
        </div>
      </div>

      <ExpandableContent expanded={expanded}>
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map((item) => (
            <div
              key={item.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 12px',
                borderRadius: 8,
                background: `${category.color}08`,
                border: `1px solid ${category.color}15`,
              }}
            >
              <div
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: category.color,
                  opacity: item.weight,
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontSize: 13,
                  color: 'var(--text-secondary)',
                  flex: 1,
                  lineHeight: 1.4,
                }}
              >
                {item.content}
              </span>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 }}>
                {formatTimeAgo(item.createdAt)}
              </span>
            </div>
          ))}
        </div>
      </ExpandableContent>
    </div>
  );
}

/* ═══════════════════════════════════════════
   页面主体
   ═══════════════════════════════════════════ */

export default function MemoryCenter() {
  const [preferences, setPreferences] = useState<PreferenceTag[]>(INITIAL_PREFERENCES);
  const [behaviors] = useState<BehaviorRecord[]>(INITIAL_BEHAVIORS);
  const [learnedPrefs] = useState<LearnedPreference[]>(LEARNED_PREFERENCES);
  const [expandedCategory, setExpandedCategory] = useState<MemoryCategory | null>(null);
  const [activeTab, setActiveTab] = useState<string>('overview');

  // 记忆分类配置
  const categories: CategoryConfig[] = useMemo(() => {
    const grouped = MEMORY_ITEMS.reduce<Record<string, MemoryItem[]>>((acc, item) => {
      if (!acc[item.category]) acc[item.category] = [];
      acc[item.category].push(item);
      return acc;
    }, {});

    const configMap: Record<MemoryCategory, { icon: string; color: string; description: string }> = {
      short: { icon: '⚡', color: '#6366f1', description: '当前会话上下文 · 实时更新' },
      long: { icon: '📚', color: '#8b5cf6', description: '跨会话持久记忆 · 历史积累' },
      preference: { icon: '💎', color: '#e8d28a', description: '静态个性偏好 · 稳定特征' },
      episodic: { icon: '🎬', color: '#34d399', description: '历史场景方案 · 事件回溯' },
    };

    return (Object.keys(configMap) as MemoryCategory[]).map((cat) => ({
      id: cat,
      name: cat === 'short' ? '短期记忆' : cat === 'long' ? '长期记忆' : cat === 'preference' ? '偏好记忆' : '场景记忆',
      icon: configMap[cat].icon,
      color: configMap[cat].color,
      description: configMap[cat].description,
      count: grouped[cat]?.length || 0,
    }));
  }, []);

  const handleRemovePreference = useCallback((id: string) => {
    setPreferences((prev) => prev.filter((p) => p.id !== id));
    message.success('偏好已移除');
  }, []);

  const handleAddPreference = useCallback((label: string, category: PreferenceTag['category']) => {
    const newPref: PreferenceTag = {
      id: `p_${Date.now()}`,
      label,
      category,
      strength: 60,
      source: 'explicit',
      createdAt: Date.now(),
    };
    setPreferences((prev) => [newPref, ...prev]);
    message.success('偏好已添加');
  }, []);

  const toggleCategory = useCallback((id: MemoryCategory) => {
    setExpandedCategory((prev) => (prev === id ? null : id));
  }, []);

  // 统计数据
  const stats = useMemo(
    () => ({
      totalMemories: MEMORY_ITEMS.length,
      preferenceCount: preferences.length,
      behaviorCount: behaviors.length,
      learnedCount: learnedPrefs.length,
    }),
    [preferences.length, behaviors.length, learnedPrefs.length]
  );

  const tabItems = [
    {
      key: 'overview',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>记忆概览</span>
        </span>
      ),
      children: (
        <div className="animate-fade-up">
          {/* 四个记忆分类卡片 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14 }}>
            {categories.map((cat) => {
              const catItems = MEMORY_ITEMS.filter((item) => item.category === cat.id);
              return (
                <MemoryCategorySection
                  key={cat.id}
                  category={{ ...cat, count: catItems.length }}
                  items={catItems}
                  expanded={expandedCategory === cat.id}
                  onToggle={() => toggleCategory(cat.id)}
                />
              );
            })}
          </div>

          {/* 统计信息 */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 12,
              marginTop: 24,
            }}
          >
            {[
              { label: '记忆总量', value: stats.totalMemories, icon: '📊', color: '#6366f1' },
              { label: '偏好标签', value: stats.preferenceCount, icon: '💎', color: '#e8d28a' },
              { label: '行为记录', value: stats.behaviorCount, icon: '📋', color: '#34d399' },
              { label: '学习成果', value: stats.learnedCount, icon: '🧠', color: '#818cf8' },
            ].map((stat) => (
              <div
                key={stat.label}
                style={{
                  padding: '16px 18px',
                  borderRadius: 14,
                  background: 'var(--surface-1)',
                  border: '1px solid var(--border-subtle)',
                  textAlign: 'center',
                }}
                className="animate-fade-up"
              >
                <div style={{ fontSize: 18, marginBottom: 6 }}>{stat.icon}</div>
                <div style={{ fontSize: 22, fontWeight: 600, color: stat.color }}>{stat.value}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      key: 'preferences',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>偏好管理</span>
          <span
            style={{
              fontSize: 11,
              padding: '1px 6px',
              borderRadius: 8,
              background: 'var(--accent-soft)',
              color: 'var(--accent)',
            }}
          >
            {preferences.length}
          </span>
        </span>
      ),
      children: (
        <div className="animate-fade-up">
          <div
            style={{
              padding: '24px',
              borderRadius: 16,
              background: 'var(--surface-1)',
              border: '1px solid var(--border-default)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>用户偏好标签</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                  管理你的个性化偏好 · AI 会根据行为自动学习更新
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  显式设置: {preferences.filter((p) => p.source === 'explicit').length}
                </span>
                <span style={{ fontSize: 11, color: 'var(--accent)' }}>
                  AI学习: {preferences.filter((p) => p.source === 'learned').length}
                </span>
              </div>
            </div>
            <PreferenceTagCloud
              preferences={preferences}
              onRemove={handleRemovePreference}
              onAdd={handleAddPreference}
              editable
            />
          </div>
        </div>
      ),
    },
    {
      key: 'history',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>行为记录</span>
          <span
            style={{
              fontSize: 11,
              padding: '1px 6px',
              borderRadius: 8,
              background: 'rgba(52, 211, 153, 0.1)',
              color: '#34d399',
            }}
          >
            {behaviors.length}
          </span>
        </span>
      ),
      children: (
        <div className="animate-fade-up">
          <div
            style={{
              padding: '24px',
              borderRadius: 16,
              background: 'var(--surface-1)',
              border: '1px solid var(--border-default)',
            }}
          >
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>历史行为记录</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                搜索、浏览、收藏、预约等行为历史 · AI 通过学习这些记录优化推荐
              </div>
            </div>
            <BehaviorHistoryList records={behaviors} />
          </div>
        </div>
      ),
    },
    {
      key: 'learned',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>AI 学习</span>
          <span
            style={{
              fontSize: 11,
              padding: '1px 6px',
              borderRadius: 8,
              background: 'rgba(129, 140, 248, 0.1)',
              color: '#818cf8',
            }}
          >
            {learnedPrefs.length}
          </span>
        </span>
      ),
      children: (
        <div className="animate-fade-up">
          <div
            style={{
              padding: '24px',
              borderRadius: 16,
              background: 'var(--surface-1)',
              border: '1px solid var(--border-default)',
            }}
          >
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>
                AI 学习到的偏好
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                基于历史行为数据自动挖掘的隐性偏好模式
              </div>
            </div>
            <LearnedPreferencesCloud preferences={learnedPrefs} />
          </div>
        </div>
      ),
    },
  ];

  return (
    <div
      style={{
        minHeight: '100vh',
        /* 透出 #root::before 的森林背景（登录页原图） */
        background: 'transparent',
        padding: '36px 28px 80px',
      }}
    >
      {/* ── 内容容器 ── */}
      <div style={{ maxWidth: 960, margin: '0 auto' }}>
        {/* ── 标题区 ── */}
        <div className="animate-fade-up" style={{ marginBottom: 36 }}>
          <h1
            style={{
              fontFamily: 'var(--font-serif, "Noto Serif SC", Georgia, serif)',
              fontSize: 'clamp(28px, 3.2vw, 36px)',
              fontWeight: 400,
              color: 'var(--text-primary)',
              margin: 0,
              marginBottom: 10,
              letterSpacing: '0.01em',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <span>记忆中心</span>
            <span style={{ fontSize: 14, fontWeight: 400, color: 'var(--accent)', opacity: 0.8 }}>
              Agent Memory System
            </span>
          </h1>
          <p
            style={{
              fontSize: 14,
              color: 'var(--text-secondary)',
              margin: 0,
              lineHeight: 1.7,
              maxWidth: 560,
            }}
          >
            分层记忆体系 · 记录用户偏好与行为 · AI 持续学习与优化推荐策略
          </p>
        </div>

        {/* ── Tab 内容区 ── */}
        <div className="animate-fade-up" style={{ animationDelay: '100ms' }}>
          <div
            style={{
              background: 'var(--night-950)',
              borderRadius: 16,
              border: '1px solid var(--border-subtle)',
              overflow: 'hidden',
            }}
          >
            {/* Tab 头部 */}
            <div
              style={{
                display: 'flex',
                borderBottom: '1px solid var(--border-subtle)',
                background: 'var(--surface-1)',
              }}
            >
              {tabItems.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  style={{
                    flex: 1,
                    padding: '14px 20px',
                    border: 'none',
                    background: activeTab === tab.key ? 'var(--surface-2)' : 'transparent',
                    color: activeTab === tab.key ? 'var(--accent)' : 'var(--text-secondary)',
                    fontSize: 14,
                    fontWeight: activeTab === tab.key ? 500 : 400,
                    cursor: 'pointer',
                    transition: 'all 200ms',
                    borderBottom: activeTab === tab.key ? '2px solid var(--accent)' : '2px solid transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                  }}
                  onMouseEnter={(e) => {
                    if (activeTab !== tab.key) {
                      (e.currentTarget as HTMLElement).style.color = 'var(--text-primary)';
                      (e.currentTarget as HTMLElement).style.background = 'var(--surface-1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeTab !== tab.key) {
                      (e.currentTarget as HTMLElement).style.color = 'var(--text-secondary)';
                      (e.currentTarget as HTMLElement).style.background = 'transparent';
                    }
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab 内容 */}
            <div style={{ padding: '28px 24px' }}>
              {tabItems.find((t) => t.key === activeTab)?.children}
            </div>
          </div>
        </div>

        {/* ── 底部说明 ── */}
        <div
          className="animate-fade-up"
          style={{
            animationDelay: '200ms',
            marginTop: 36,
            textAlign: 'center',
            padding: '16px 0',
          }}
        >
          <p
            style={{
              fontSize: 11,
              color: 'var(--text-muted)',
              margin: 0,
              letterSpacing: '0.04em',
            }}
          >
            Agent Memory System · 记忆数据本地存储 · 自动同步云端
          </p>
        </div>
      </div>
    </div>
  );
}
